import { useParams, Navigate } from "react-router-dom"
import { usePageTitle } from "@/hooks/use-page-title"
import {
  DetailHeroSection,
  OverviewSection,
  PlatformShowcaseSection,
  HowItWorksSection,
  BenefitsSection,
  RelatedUseCasesSection,
} from "@/components/use-cases/detail"
import { CtaSection } from "@/components/use-cases/cta-section"
import { FooterSection } from "@/components/home/footer"
import { useCasesDetailed, hasDetailedData } from "@/data/use-cases-data"
import type { UseCaseDetail } from "@/components/use-cases/types"

/**
 * Use Case detail page component.
 * Renders all sections for a specific use case based on URL slug.
 */
export function UseCaseDetailPage() {
  const { slug } = useParams<{ slug: string }>()

  // Find the use case by slug
  const useCase = useCasesDetailed.find((uc) => uc.slug === slug) as UseCaseDetail | undefined

  // Set page title
  usePageTitle(useCase?.title ?? "Use Case")

  // If no slug or use case not found, redirect to listing
  if (!slug || !useCase || !hasDetailedData(slug)) {
    return <Navigate to="/use-cases" replace />
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <DetailHeroSection hero={useCase.hero} />

      {/* Overview Section */}
      <OverviewSection overview={useCase.overview} />

      {/* Platform Showcase Section */}
      <PlatformShowcaseSection
        agentConfig={useCase.agentConfig}
        workflowConfig={useCase.workflowConfig}
      />

      {/* How It Works Section */}
      <HowItWorksSection steps={useCase.howItWorks} />

      {/* Benefits Section */}
      <BenefitsSection benefits={useCase.benefits} />

      {/* Related Use Cases Section */}
      <RelatedUseCasesSection
        relatedSlugs={useCase.relatedSlugs}
        currentSlug={useCase.slug}
      />

      {/* CTA Section */}
      <CtaSection />

      {/* Footer */}
      <FooterSection />
    </div>
  )
}
